// Path: ./src/app/pages/EmailNotifications/index.tsx
import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  Edit as EditIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import {
  CreateNotificationRecipientDto,
  GfNotificationRecipient,
  NotificationTypeDto,
  UpdateNotificationRecipientDto,
} from "@swagger/typescript-fetch-goflyer";
import { adminApiService } from "../../../utils/api";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import { toastSuccess } from "../../../utils/pageUtil/toast";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import {
  brandSx,
  RecipientDialog,
  RecipientFormValues,
} from "./RecipientDialog";

type DialogState =
  | { mode: "closed" }
  | { mode: "add" }
  | { mode: "edit"; recipient: GfNotificationRecipient };

/** The SDK types notificationTypes as generated enums; the values are the type keys. */
const toApiTypes = (types: string[]) =>
  types as unknown as CreateNotificationRecipientDto["notificationTypes"];

const toFormValues = (
  recipient: GfNotificationRecipient,
): RecipientFormValues => ({
  email: recipient.email,
  notificationTypes: recipient.notificationTypes.map(String),
  enabled: recipient.enabled,
});

/**
 * Who receives system alert emails, and which alerts each address gets.
 * Failed API calls are toasted by the adminApiService wrapper, which then
 * resolves to undefined.
 */
export const EmailNotificationsPage = () => {
  const authHeaders = useAuthHeaders();
  const [recipients, setRecipients] = useState<GfNotificationRecipient[]>([]);
  const [types, setTypes] = useState<NotificationTypeDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState<DialogState>({ mode: "closed" });
  const [saving, setSaving] = useState(false);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<GfNotificationRecipient | null>(
    null,
  );

  const typeLabel = (key: string) =>
    types.find((type) => String(type.key) === key)?.label ?? key;

  const load = useCallback(async () => {
    setLoading(true);
    const [typeList, recipientList] = await Promise.all([
      adminApiService.gfNotificationRecipientAdminControllerGetTypes(
        authHeaders,
      ),
      adminApiService.gfNotificationRecipientAdminControllerFindAll(
        authHeaders,
      ),
    ]);
    setTypes(typeList ?? []);
    setRecipients(recipientList ?? []);
    setLoading(false);
  }, [authHeaders]);

  useEffect(() => {
    load();
  }, [load]);

  const replaceRecipient = (updated: GfNotificationRecipient) =>
    setRecipients((current) =>
      current.map((recipient) =>
        recipient.id === updated.id ? updated : recipient,
      ),
    );

  const handleSave = async (values: RecipientFormValues) => {
    setSaving(true);
    const body = {
      email: values.email,
      notificationTypes: toApiTypes(values.notificationTypes),
      enabled: values.enabled,
    };
    if (dialog.mode === "edit") {
      const updated =
        await adminApiService.gfNotificationRecipientAdminControllerUpdate(
          body as UpdateNotificationRecipientDto,
          dialog.recipient.id,
          authHeaders,
        );
      if (updated) {
        replaceRecipient(updated);
        toastSuccess(`Saved ${updated.email}`);
        setDialog({ mode: "closed" });
      }
    } else {
      const created =
        await adminApiService.gfNotificationRecipientAdminControllerCreate(
          body,
          authHeaders,
        );
      if (created) {
        setRecipients((current) =>
          [...current, created].sort((a, b) => a.email.localeCompare(b.email)),
        );
        toastSuccess(`Added ${created.email}`);
        setDialog({ mode: "closed" });
      }
    }
    setSaving(false);
  };

  const handleToggleEnabled = async (recipient: GfNotificationRecipient) => {
    setPendingId(recipient.id);
    const updated =
      await adminApiService.gfNotificationRecipientAdminControllerUpdate(
        { enabled: !recipient.enabled },
        recipient.id,
        authHeaders,
      );
    if (updated) {
      replaceRecipient(updated);
      toastSuccess(
        `${updated.email} ${updated.enabled ? "enabled" : "disabled"}`,
      );
    }
    setPendingId(null);
  };

  const handleSendTest = async (recipient: GfNotificationRecipient) => {
    setPendingId(recipient.id);
    const sent =
      await adminApiService.gfNotificationRecipientAdminControllerSendTestEmail(
        recipient.id,
        authHeaders,
      );
    if (sent) {
      toastSuccess(`Test email sent to ${recipient.email}`);
    }
    setPendingId(null);
  };

  const handleRemove = async () => {
    if (!removing) {
      return;
    }
    setSaving(true);
    const removed =
      await adminApiService.gfNotificationRecipientAdminControllerRemove(
        removing.id,
        authHeaders,
      );
    if (removed) {
      setRecipients((current) =>
        current.filter((recipient) => recipient.id !== removing.id),
      );
      toastSuccess(`Removed ${removing.email}`);
      setRemoving(null);
    }
    setSaving(false);
  };

  const editingId = dialog.mode === "edit" ? dialog.recipient.id : null;
  const takenEmails = recipients
    .filter((recipient) => recipient.id !== editingId)
    .map((recipient) => recipient.email.toLowerCase());

  return (
    <Box sx={{ mt: "65px", minHeight: "100vh", bgcolor: "#fafafa" }}>
      <AccountCardWrapper title="Email Notifications">
        <Box sx={{ p: 2.5 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-between",
              gap: 2,
              mb: 2.5,
            }}
          >
            <Box>
              <Typography
                component="h1"
                variant="h5"
                sx={{ fontWeight: 600, mb: 0.5, color: "#1a1a1a" }}
              >
                Email Notifications
              </Typography>
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                Choose who gets system alert emails and which alerts each
                address gets. Changes apply to the next alert, with no restart.
              </Typography>
            </Box>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setDialog({ mode: "add" })}
              disabled={loading}
              sx={{ ...brandSx.containedButton, flexShrink: 0 }}
            >
              Add email
            </Button>
          </Box>

          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress
                aria-label="Loading recipients"
                sx={brandSx.spinner}
              />
            </Box>
          ) : recipients.length === 0 ? (
            <Box
              sx={{
                py: 6,
                textAlign: "center",
                border: "1px dashed #d1d5db",
                borderRadius: 1,
              }}
            >
              <Typography sx={{ fontWeight: 600, mb: 0.5 }}>
                No one receives alert emails
              </Typography>
              <Typography variant="body2" sx={{ color: "#4b5563" }}>
                Add an email and pick the alerts it should get.
              </Typography>
            </Box>
          ) : (
            <TableContainer
              sx={{ border: "1px solid #e5e7eb", borderRadius: 1 }}
            >
              <Table size="small" aria-label="Alert email recipients">
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Notifications</TableCell>
                    <TableCell>Enabled</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {recipients.map((recipient) => {
                    const busy = pendingId === recipient.id;
                    return (
                      <TableRow key={recipient.id}>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {recipient.email}
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}
                          >
                            {recipient.notificationTypes.map((type) => (
                              <Chip
                                key={String(type)}
                                size="small"
                                label={typeLabel(String(type))}
                              />
                            ))}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Switch
                            checked={recipient.enabled}
                            disabled={busy}
                            sx={brandSx.switch}
                            onChange={() => handleToggleEnabled(recipient)}
                            inputProps={{
                              "aria-label": `Send alerts to ${recipient.email}`,
                            }}
                          />
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: "nowrap" }}>
                          <Tooltip title="Send test email">
                            <span>
                              <IconButton
                                aria-label={`Send test email to ${recipient.email}`}
                                disabled={busy}
                                onClick={() => handleSendTest(recipient)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              aria-label={`Edit ${recipient.email}`}
                              onClick={() =>
                                setDialog({ mode: "edit", recipient })
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Remove">
                            <IconButton
                              aria-label={`Remove ${recipient.email}`}
                              onClick={() => setRemoving(recipient)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Box>
      </AccountCardWrapper>

      {dialog.mode !== "closed" && (
        <RecipientDialog
          initialValues={
            dialog.mode === "edit" ? toFormValues(dialog.recipient) : undefined
          }
          types={types}
          takenEmails={takenEmails}
          saving={saving}
          onCancel={() => setDialog({ mode: "closed" })}
          onSave={handleSave}
        />
      )}

      <Dialog
        open={!!removing}
        onClose={saving ? undefined : () => setRemoving(null)}
        aria-labelledby="remove-recipient-title"
      >
        <DialogTitle id="remove-recipient-title">Remove email?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {removing?.email} will stop getting alert emails right away.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setRemoving(null)}
            disabled={saving}
            sx={brandSx.textButton}
          >
            Cancel
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleRemove}
            disabled={saving}
          >
            Remove
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
